import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { withFocus } from '@accedo/vdkweb-navigation';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { getTopics } from '#/redux/modules/firestore/selectors';
import { getEntryById } from '#/redux/modules/accedoOne/selectors';

import getVw from '#/utils/getVw';
import { subRoutes } from '#/utils/routes';
import {
  containersTypeAlias,
  SWIMLANE_LENGTH,
  SWIMLANE_RIGHT_ARROW,
  SWIMLANE_LEFT_ARROW,
  mainPageNames,
} from '#/config/constants';

import TopicCard from '#/components/TopicCard/TopicCard';
import topicCardStyles from '#/components/TopicCard/topicCard.scss';
import Swimlane from '#/components/Swimlane/Swimlane';
import styles from './topicSwimlaneFactory.scss';
import { isWorkstation } from '#/utils/componentStyleConfig';
import { isItemOutsideScreen } from '#/utils/isItemOutsideScreen';
import { FocusDiv } from '#/widgets/ui';

import { getComponentItems } from '../swimlaneFactoryUtils';

const SeeAllCard = ({ nav, onClick, className, style, children }) => {
  return (
    <div
      id={nav.id}
      onClick={onClick}
      className={classNames(className, { [styles.isFocused]: nav.isFocused })}
      style={style}
    >
      {children}
    </div>
  );
};
const FocusSeeAllCard = withFocus(SeeAllCard);
SeeAllCard.propTypes = {
  nav: PropTypes.object,
  onClick: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.any,
  style: PropTypes.any,
};

// topic data loaded on profile switch from TVAppContent
const TopicSwimlaneFactory = ({
  topics,
  title,
  nav,
  ageTheme,
  history,
  enableSeeAll = true,
  alwaysVisible = false,
  onFocus,
  location,
  useLastFocus = true,
  containerId,
}) => {
  const items = topics || useSelector(getTopics) || [];
  const { separatorColor, backgroundColor } =
    useSelector(state => getEntryById(state, containerId)) || {};
  const getItemId = useCallback(index => `${nav.id}-TOPIC-${index}`, []);
  const SEE_ALL = `${nav.id}-see-all`;
  const currentIndex = useRef(0);
  const previousIndex = useRef(0);
  const currentPath = location?.pathname?.split('/').slice(-1)[0];

  const [rowChanged, setRowChanged] = useState(true);
  const itemFocusChanged = useRef(() => {});
  const setFocusChanged = useCallback(func => {
    itemFocusChanged.current = func;
  }, []);

  const getImagePath = useCallback(
    item =>
      item?.images?.find(e => e.age === ageTheme?.minAge).image_path ||
      item?.image_path ||
      '',
    [ageTheme],
  );
  const getPlaylistId = useCallback(
    item =>
      item?.playlists?.find(e => e.age === ageTheme?.minAge)?.playlist_id ||
      '0',
    [ageTheme],
  );

  const onItemFocus = useCallback(
    id => {
      if (onFocus) {
        onFocus(nav.id);
      }

      // If no id then assume we're jumping to the first tile
      id = id ?? `${nav.id}-VIDEO-0`;

      previousIndex.current = currentIndex.current;
      currentIndex.current = id;

      itemFocusChanged.current(id);
    },
    [onFocus, nav.id],
  );

  const handleNavigation = useCallback((nextIndex, itemsLength) => {
    if (nextIndex < 0 || nextIndex > itemsLength - 1) {
      return;
    }
    setRowChanged(false);
    currentIndex.current = nextIndex;

    return false;
  }, []);

  useEffect(() => {
    previousIndex.current = 0;
    currentIndex.current = 0;

    if (!useLastFocus) {
      itemFocusChanged.current(getItemId(currentIndex.current));
    }
  }, [items]);

  const generateItemNavs = useCallback(
    i => {
      const itemsLimit = items.limit || SWIMLANE_LENGTH;
      return {
        parent: nav.id,
        id: getItemId(i),
        ...(i !== 0 ? { nextleft: getItemId(i - 1) } : {}),
        ...(i + 1 === itemsLimit && enableSeeAll
          ? { nextright: SEE_ALL }
          : i !== items.length - 1
          ? { nextright: getItemId(i + 1) }
          : {}),
        internal: {
          nextright: () => handleNavigation(i + 1, items.length),
          nextleft: () => handleNavigation(i - 1, items.length),
          nextup: () => setRowChanged(true),
          nextdown: () => setRowChanged(true),
        },
      };
    },
    [items],
  );

  useEffect(() => {
    const itemsLimit = items.limit || SWIMLANE_LENGTH;
    const currentItemId =
      currentIndex.current === itemsLimit && enableSeeAll
        ? SEE_ALL
        : getItemId(currentIndex.current);
    if (!isWorkstation() && isItemOutsideScreen(currentItemId)) {
      if (previousIndex.current < currentIndex.current) {
        document.getElementById(SWIMLANE_RIGHT_ARROW + nav.id)?.click();
      } else if (previousIndex.current > currentIndex.current) {
        document.getElementById(SWIMLANE_LEFT_ARROW + nav.id)?.click();
      }
    }
    previousIndex.current = currentIndex.current;
  }, [currentIndex, previousIndex, items]);

  const createAriaLabel = useCallback(
    item => {
      const label = `${item.name}`;
      if (rowChanged) {
        return `${title || 'Channels'}, ${label}`;
      }
      return label;
    },
    [rowChanged],
  );

  const [topicCards, emptyItems] = useMemo(() => {
    const itemsLimit = items.limit || SWIMLANE_LENGTH;
    const [cards, emptyCards] = getComponentItems(
      items.slice(0, enableSeeAll ? itemsLimit : items.length),
      (item, index) => {
        const commonProps = {
          nav: generateItemNavs(index),
          key: getItemId(index),
          ariaLabel: createAriaLabel(item),
        };

        return [
          <TopicCard
            {...commonProps}
            className={classNames(styles.cardContainer, currentPath || '')}
            id={item.id}
            title={item.name}
            imgUrl={getImagePath(item)}
            roundedCorners={ageTheme?.tileRounded}
            onItemFocus={onItemFocus}
            playlistId={getPlaylistId(item)}
          />,
          <FocusDiv
            className={classNames(
              topicCardStyles.card,
              topicCardStyles.empty,
              currentPath || '',
              {
                [topicCardStyles.isTv]: !isWorkstation(),
              },
            )}
            classNameFocused={topicCardStyles.focused}
            onFocus={onItemFocus}
          />,
        ];
      },
    );

    if (items.length > itemsLimit && enableSeeAll) {
      const path = history.location?.pathname?.split('/');
      const containerAlias =
        currentPath === mainPageNames.FAVORITES && !isWorkstation()
          ? containersTypeAlias.TVCONTAINER_FAV_TOPICS
          : containersTypeAlias.CONTAINER_TOPICS;

      emptyCards.push(
        <FocusDiv
          className={classNames(
            topicCardStyles.card,
            topicCardStyles.empty,
            currentPath || '',
            {
              [topicCardStyles.isTv]: !isWorkstation(),
            },
          )}
          classNameFocused={topicCardStyles.focused}
          style={{ borderRadius: ageTheme?.tileRounded ? getVw(24) : '0px' }}
          nav={{
            id: SEE_ALL,
            nextleft: getItemId(itemsLimit - 1),
            parent: nav.id,
          }}
          key={SEE_ALL}
          onFocus={onItemFocus}
        />,
      );

      cards.push(
        <FocusSeeAllCard
          nav={{
            id: SEE_ALL,
            nextleft: getItemId(itemsLimit - 1),
            parent: nav.id,
          }}
          key={SEE_ALL}
          onFocus={onItemFocus}
          onClick={() => {
            history.push(
              `/${path[1]}/${path[2]}/${subRoutes.SEE_ALL}/${items[itemsLimit].id}/${title}/${containerAlias}`,
            );
          }}
          className={classNames(styles.seeAllTopics, {
            [styles.isTv]: !isWorkstation(),
          })}
          style={{ borderRadius: ageTheme?.tileRounded ? getVw(24) : '0px' }}
        >
          <div
            className={styles.seeAllImage}
            style={{
              backgroundImage: `url(${getImagePath(items[itemsLimit])})`,
            }}
          >
            <div />
          </div>
          <div className={styles.seeAllText}>
            <p>See all</p>
          </div>
        </FocusSeeAllCard>,
      );
    }

    return [cards, emptyCards];
  }, [items, rowChanged, onItemFocus]);

  const topicsSwimlaneNav = {
    ...nav,
    forwardFocus: `${nav.id}-TOPIC-0`,
    useLastFocus,
  };
  return topicCards.length ? (
    <Swimlane
      isTv={!isWorkstation()}
      alwaysVisible={alwaysVisible}
      items={topicCards}
      emptyItems={emptyItems}
      title={title}
      nav={topicsSwimlaneNav}
      navId={nav.id}
      setFocusChanged={setFocusChanged}
      separatorColor={separatorColor}
      backgroundColor={backgroundColor}
    />
  ) : null;
};
TopicSwimlaneFactory.propTypes = {
  topics: PropTypes.array,
  title: PropTypes.string,
  nav: PropTypes.object,
  ageTheme: PropTypes.object,
  history: PropTypes.object,
  enableSeeAll: PropTypes.bool,
  alwaysVisible: PropTypes.bool,
  onFocus: PropTypes.func,
  location: PropTypes.object,
  useLastFocus: PropTypes.bool,
  containerId: PropTypes.string,
};

export default withRouter(TopicSwimlaneFactory);
